import { ChangeEvent, useRef, useState } from 'react';
import { CustomFloatingFilterProps } from 'ag-grid-react';

import { REQUISITION_ID_REGEX } from '@constants/constants';
import { useDebounceCallback } from '@hooks/useDebounce';

/**
 * Floating filter for the requisition_id column
 * Validates the input is a valid requisition ID before attempting to make an API call
 * Also debounces entries to prevent excessive API calls
 */

interface RequisitionIdFloatingFilterProps extends CustomFloatingFilterProps {
  debouncMs?: number;
}

export const RequisitionIdFloatingFilter: React.FC<RequisitionIdFloatingFilterProps> = props => {
  const { debouncMs = 500, model, onModelChange } = props;

  const [value, setValue] = useState<string>(model || '');
  const wasValidRef = useRef<boolean>(false);

  const handleModelChange = useDebounceCallback((newValue: string) => {
    const isValid = REQUISITION_ID_REGEX.test(newValue);
    const wasValid = wasValidRef.current;

    if (isValid !== wasValid) {
      wasValidRef.current = isValid;
    }

    if (isValid) {
      onModelChange({
        type: 'equals',
        filter: newValue,
      });
    } else if (wasValid) {
      onModelChange({
        type: 'equals',
        filter: null,
      });
    }
  }, debouncMs);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setValue(newValue);
    handleModelChange(newValue);
  };

  return (
    <input
      aria-label='Filter by Order ID'
      className='ag-input-field-input ag-text-field-input'
      name='requisition_id'
      onChange={handleChange}
      pattern={REQUISITION_ID_REGEX.source}
      type='text'
      value={value}
    />
  );
};
